import { api } from "./api";
import {
  ListRequest,
  ListRequestSchemaV2,
  PaginatedResponse,
  MessageResponse,
  BaseUpdateResponseSchemaV2,
} from "../types/common/commonTypes";
import {
  InvLotFull,
  InvLotListView,
  PaginatedLotResponse,
  InvLotUpdate,
} from "../types/lotTypes";
import { FullLotCreate } from "../types/combinedTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const lot_base = "inventory/lots";
export const lotService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================
  list: async (params: ListRequest): Promise<PaginatedLotResponse> => {
    const response = await api.post(`${lot_base}/search`, params);
    return response.data;
  },
  listv2: async (
    params: ListRequestSchemaV2,
  ): Promise<PaginatedResponse<InvLotListView>> => {
    const response = await api.post(`${lot_base}/list`, params);
    return response.data;
  },
  listPalletLots: async (): Promise<PaginatedResponse<InvLotListView>> => {
    const response = await api.get(`${lot_base}/pallet_lots`);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<InvLotFull> => {
    const response = await api.get(`${lot_base}/${id}`);
    return response.data;
  },
  getByNum: async (num: string): Promise<InvLotFull> => {
    const response = await api.get(`${lot_base}/by_num/${num}`);
    return response.data;
  },

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: FullLotCreate): Promise<InvLotFull> => {
    const response = await api.post(`${lot_base}/`, data);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (id: string, data: InvLotUpdate): Promise<InvLotFull> => {
    const response = await api.put(`${lot_base}/${id}`, { ...data, id });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${lot_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<InvLotFull> => {
    const response = await api.post(`${lot_base}/${id}/restore`);
    return response.data;
  },
};
